import React from "react";
import { values } from "mobx";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";

import {
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@material-ui/core";

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis
} from "recharts";

const styles = theme => ({
  pageName: {
    fontWeight: "bold"
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  readingGraph: {
    padding: theme.spacing(2)
  },
  lastMeasurementPaper: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column"
  },
  lastMeasurementContainer: {
    padding: theme.spacing(4),
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
  },
  lastMeasurementTitle: {
    marginBottom: theme.spacing(2)
  },
  lastMeasurementValue: {
    marginBottom: -theme.spacing(1)
  }
});

class SensorGroupInfo extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      table: {
        rowsPerPage: 5,
        currentPage: 0
      }
    };
  }

  setRowsPerPage(rowsPerPage) {
    this.setState(state => ({
      ...state,
      table: {
        ...state.table,
        rowsPerPage: rowsPerPage
      }
    }));
  }

  setCurrentPage(currentPage) {
    this.setState(state => ({
      ...state,
      table: {
        ...state.table,
        currentPage: currentPage
      }
    }));
  }

  componentDidMount() {
    const sensorStore = this.props.rootStore.sensorStore;
    const sensorGroupStore = this.props.rootStore.sensorGroupStore;

    this.setState(state => ({
      ...state,
      loading: true
    }));

    sensorStore.fetchAndUpdate().then(() => {
      sensorGroupStore.fetchAndUpdate().then(() => {
        const sensorGroup = sensorGroupStore.sensor_groups.get(this.props.id);
        sensorGroup.fetchAndUpdateReadings().then(() => {
          this.setState(state => ({
            ...state,
            loading: false
          }));
        });
      });
    });
  }

  render() {
    const classes = this.props.classes;

    if (this.state.loading) {
      return (
        <div className={classes.progressContainer}>
          <CircularProgress className={classes.progress} />
        </div>
      );
    }

    const sensorGroup =
      this.props.rootStore.sensorGroupStore.sensor_groups.get(this.props.id) ||
      {};
    const readings = sensorGroup.readings || [];
    const sensors = this.props.rootStore.sensorStore.sensors;

    const data = readings
      .slice()
      .reverse()
      .map(
        r =>
          new Map(
            Object.entries({
              timestamp: Number(moment(r.end).valueOf()),
              ppm: Number((r.payload.ppm || 0).toFixed(1))
            })
          )
      )
      .map(reading => {
        if (this.props.user.is_staff) {
          return reading;
        } else {
          return reading.set("ppm", Math.max(reading.get("ppm"), 0.1));
        }
      });

    const renderLineChart = (
      <ResponsiveContainer aspect={2.7} width={"100%"}>
        <LineChart data={data}>
          <XAxis
            dataKey={reading => reading.get("timestamp")}
            domain={["dataMin", "dataMax"]}
            type="number"
            tickFormatter={tick => moment(tick).format("l HH:mma")}
          />
          <YAxis
            dataKey={reading => reading.get("ppm")}
            type="number"
            domain={[0, 5]}
          />
          <Line
            type="monotone"
            stroke="#adc537"
            strokeWidth={4}
            dataKey={reading => reading.get("ppm")}
            fillOpacity={0.5}
            dot={false}
          />
          <CartesianGrid strokeDasharray="3 3" />
        </LineChart>
      </ResponsiveContainer>
    );

    const tableStart =
      this.state.table.currentPage * this.state.table.rowsPerPage;
    const renderTable = (
      <React.Fragment>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Ripeness</TableCell>
              <TableCell align="right">Timestamp</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data
              .slice()
              .reverse()
              .slice(tableStart, tableStart + this.state.table.rowsPerPage)
              .map(reading => (
                <TableRow key={reading.id}>
                  <TableCell component="th" scope="row">
                    {reading.get("ppm")}
                  </TableCell>
                  <TableCell align="right">
                    {moment(reading.get("timestamp")).format("L hh:mm:ssa")}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={data.length}
          rowsPerPage={this.state.table.rowsPerPage}
          page={this.state.table.currentPage}
          backIconButtonProps={{
            "aria-label": "Previous Page"
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page"
          }}
          onChangePage={(e, newPage) => this.setCurrentPage(newPage)}
          onChangeRowsPerPage={e => this.setRowsPerPage(e.target.value)}
        />
      </React.Fragment>
    );

    const lastMeasurement = data.length ? data[data.length - 1].get("ppm") : 0;

    const includedSensorIds = (this.props.user || {}).is_staff
      ? (sensorGroup.sensors || []).map(s => s.id)
      : [];
    const includedSensorsEditor = (
      <Grid item xs={12}>
        <Typography variant="h5">Included Devices</Typography>
        <br />
        <Paper>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Device Name</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {values(sensors)
                .sort((a, b) => a.human_name.localeCompare(b.human_name))
                .map(sensor => {
                  const included = includedSensorIds.includes(sensor.id);
                  return (
                    <TableRow key={sensor.id}>
                      <TableCell>{sensor.human_name}</TableCell>
                      <TableCell align="right">
                        <Switch
                          checked={included}
                          onChange={() =>
                            included
                              ? sensorGroup.removeSensorsByIds([sensor.id])
                              : sensorGroup.addSensorsByIds([sensor.id])
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Paper>
      </Grid>
    );

    return (
      <React.Fragment>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" className={classes.pageName}>
              {sensorGroup.human_name || ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper className={classes.lastMeasurementPaper}>
              <Typography variant="h6" className={classes.lastMeasurementTitle}>
                Last Measurement
              </Typography>
              <Divider />
              <div className={classes.lastMeasurementContainer}>
                <Typography
                  variant="h2"
                  className={classes.lastMeasurementValue}
                >
                  {lastMeasurement}
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.lastMeasurementUnits}
                >
                  PPM
                </Typography>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper className={classes.readingGraph}>{renderLineChart}</Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.sensorMeasurements}>{renderTable}</Paper>
          </Grid>
          {(this.props.user || {}).is_staff && includedSensorsEditor}
        </Grid>
      </React.Fragment>
    );

    /*
    return (
      <React.Fragment>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" className={classes.sensorName}>
              {sensor_group.human_name || ""}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper className={classes.sensorLastMeasurementPaper}>
              <Typography
                variant="h6"
                className={classes.sensorLastMeasurementTitle}
              >
                Last Measurement
              </Typography>
              <Divider />
              <div className={classes.sensorLastMeasurementContainer}>
                <Typography
                  variant="h2"
                  className={classes.sensorLastMeasurementValue}
                >
                  {lastMeasurement}
                </Typography>
                <Typography
                  variant="subtitle1"
                  className={classes.sensorLastMeasurementUnits}
                >
                  PPM
                </Typography>
              </div>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper className={classes.sensorGraph}>{renderLineChart}</Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.sensorMeasurements}>{renderTable}</Paper>
          </Grid>
          {(this.props.user || {}).is_staff && (
            <Grid item xs={12}>
              <Paper className={classes.sensorMeasurements}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {this.state.sensors.map(sensor => (
                      <TableRow key={sensor.id}>
                        <TableCell component="th" scope="row">
                          {sensor.human_name}
                        </TableCell>
                        <TableCell align="right">
                          <div className={classes.iconTableWrapper}>
                            <Switch
                              checked={includedSensorIDs.has(sensor.id)}
                              onChange={() =>
                                includedSensorIDs.has(sensor.id)
                                  ? this.removeSensorFromRoom(sensor.id)
                                  : this.addSensorToRoom(sensor.id)
                              }
                            />
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}
        </Grid>
      </React.Fragment>
    );
    */
  }
}

export default withStyles(styles)(
  inject(stores => ({
    user: stores.rootStore.authStore.user,
    rootStore: stores.rootStore
  }))(observer(SensorGroupInfo))
);
